import { resolveBaseToken } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { APPBaseTokenConfig } from '@/client/types/app';
import { useEffect, useMemo } from 'react';

// Canonical resolver lives in confs/app (also used by the store, where a
// hooks-import would be a cycle); re-export for existing consumers.
export { resolveBaseToken };

// A market's own base token, falling back to the global toggle token when the
// market doesn't carry one (single-base tenants / data not yet loaded).
export const useMarketBaseToken = (address?: string): APPBaseTokenConfig => {
  const globalToken = useAppSettingStore(state => state.baseToken);
  return useMemo(() => resolveBaseToken(address) || globalToken, [address, globalToken]);
};

// Detail pages point the global wallet lens at the viewed market's token so
// header balance / deposit match what's being traded — but the visitor's own
// choice must survive the visit: snapshot it on mount, restore on leave.
// While mounted the lens is LOCKED (a market's collateral is fixed, so a
// manual toggle would desync header balance/deposit from the trade); pin and
// restore go through the store's force path, which bypasses that lock.
export const useMarketLensSync = (marketTokenAddress?: string) => {
  useEffect(() => {
    const { acquireLensLock } = useAppSettingStore.getState();
    const entryAddress = useAppSettingStore.getState().baseToken.address;
    acquireLensLock();
    return () => {
      const { releaseLensLock, forceBaseTokenAddress } = useAppSettingStore.getState();
      releaseLensLock();
      if (entryAddress) forceBaseTokenAddress(entryAddress);
    };
  }, []);

  useEffect(() => {
    if (marketTokenAddress) useAppSettingStore.getState().forceBaseTokenAddress(marketTokenAddress);
  }, [marketTokenAddress]);
};
